import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Search, Loader2, FileText, Package, Plus, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALC_API = `${API}/order-book-reports/ho-support/calculator`;
const ORANGE = '#ee7203';

const gbp = (v) => `£${(Number(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch (_) { return iso; }
};

/**
 * Turns one line of a quote / order into a complete H/O Support Calculator line, doing exactly what picking a product in
 * the Add Line window does: list price, discount and rebate from Settings > Pricing, the UOM multiplier from the product's
 * cost factor when it is priced per M2 / length, the supplier group, and the H/O support % suggested by the Supplier
 * Matrix. The selling price is the price the customer was actually quoted / charged (per each), so the contribution
 * before and after H/O support is worked from the real price.
 * Returns { line, notes } — notes explain anything that could not be filled in.
 */
async function buildCalcLine(src) {
  const code = String(src.code || '').trim().toUpperCase();
  const line = {
    id: null,
    code,
    description: src.description || '',
    supplier_name: '',
    quantity: Number(src.quantity) > 0 ? Number(src.quantity) : 1,
    unit: 'Each',
    list_price: 0,
    uom_multiplier: 1,
    discount_pct: 0,
    rebate_pct: 0,
    ho_support_pct: 0,
    price_mode: 'selling',
    margin_pct: 0,
    selling_price: Number(src.selling_price) > 0 ? Number(src.selling_price) : 0,
  };
  const notes = [];
  if (!code) {
    notes.push('no product code — enter the list price by hand');
    return { line, notes };
  }
  const [pricing, ho, cf] = await Promise.allSettled([
    api.get(`${API}/pricing/lookup/${encodeURIComponent(code)}`),
    api.get(`${API}/order-book-reports/ho-support/product-lookup`, { params: { code } }),
    api.get(`${API}/stock-database/cost-factor/${encodeURIComponent(code)}`),
  ]);
  const pr = pricing.status === 'fulfilled' ? pricing.value.data : null;
  const hs = ho.status === 'fulfilled' ? ho.value.data : null;
  const cfd = cf.status === 'fulfilled' ? cf.value.data : null;
  if (pr?.found) {
    const costFactor = cfd?.found ? parseFloat(cfd.cost_factor) : NaN;
    const needsCf = pr.quote_uom && pr.quote_uom !== 'Each' && costFactor > 0;
    line.list_price = pr.list_price;
    line.discount_pct = pr.discount_pct || 0;
    line.rebate_pct = pr.rebate_pct || 0;
    line.uom_multiplier = needsCf ? costFactor : 1;
    if (pr.supplier_name) line.supplier_name = pr.supplier_name;
    if (pr.quote_uom && pr.quote_uom !== 'Each' && !needsCf) {
      notes.push(`priced per ${pr.quote_uom} with no cost factor — set the UOM multiplier by hand`);
    }
  } else {
    notes.push('not priced in Settings > Pricing — enter the list price by hand');
  }
  if (hs?.matched) {
    line.ho_support_pct = Math.round(hs.suggested_pct * 10000) / 100;
    if (!line.supplier_name && hs.supplier_code) line.supplier_name = hs.supplier_code;
  }
  return { line, notes };
}

/** "Add from Quote / Order" — pick a recent quote or order on the system, tick the lines you want and add them all to the
 * calculation in one go. */
export default function HOSupportAddFromDocumentModal({ isOpen, customer, onClose, onAddLines }) {
  const [scope, setScope] = useState('customer');          // 'customer' | 'all'
  const [kind, setKind] = useState('all');                 // 'all' | 'quote' | 'order'
  const [query, setQuery] = useState('');
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [openDoc, setOpenDoc] = useState(null);            // the document whose lines are showing
  const [docLines, setDocLines] = useState([]);
  const [linesLoading, setLinesLoading] = useState(false);
  const [checked, setChecked] = useState({});              // line index -> true
  const [adding, setAdding] = useState(false);
  const seqRef = useRef(0);

  const hasCustomer = !!(customer?.id || customer?.name);

  useEffect(() => {
    if (!isOpen) return;
    setScope(hasCustomer ? 'customer' : 'all');
    setKind('all');
    setQuery('');
    setDocs([]);
    setOpenDoc(null);
    setDocLines([]);
    setChecked({});
    setAdding(false);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search of recent quotes / orders
  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(async () => {
      const seq = ++seqRef.current;
      setDocsLoading(true);
      try {
        const params = { q: query.trim(), kind };
        if (scope === 'customer' && hasCustomer) {
          params.account_id = customer?.id || '';
          params.customer_name = customer?.name || '';
        }
        const res = await api.get(`${CALC_API}/source-documents`, { params });
        if (seq === seqRef.current) setDocs(res.data?.documents || []);
      } catch (_) {
        if (seq === seqRef.current) { setDocs([]); toast.error('Could not load recent quotes and orders'); }
      } finally {
        if (seq === seqRef.current) setDocsLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [isOpen, query, kind, scope]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDocument = async (doc) => {
    setOpenDoc(doc);
    setDocLines([]);
    setChecked({});
    setLinesLoading(true);
    try {
      const res = await api.get(`${CALC_API}/source-documents/${doc.source}/${doc.id}`);
      const lines = res.data?.lines || [];
      setDocLines(lines);
      const all = {};
      lines.forEach((_, i) => { all[i] = true; });
      setChecked(all);
    } catch (_) {
      toast.error('Could not open that quote / order');
      setOpenDoc(null);
    } finally {
      setLinesLoading(false);
    }
  };

  const selectedCount = useMemo(() => docLines.filter((_, i) => checked[i]).length, [docLines, checked]);
  const allChecked = docLines.length > 0 && selectedCount === docLines.length;
  const toggleAll = () => {
    const next = {};
    if (!allChecked) docLines.forEach((_, i) => { next[i] = true; });
    setChecked(next);
  };

  const handleAdd = async () => {
    const chosen = docLines.filter((_, i) => checked[i]);
    if (chosen.length === 0) { toast.error('Tick at least one line'); return; }
    setAdding(true);
    try {
      const built = [];
      for (let i = 0; i < chosen.length; i += 4) {
        // eslint-disable-next-line no-await-in-loop
        const batch = await Promise.all(chosen.slice(i, i + 4).map(buildCalcLine));
        built.push(...batch);
      }
      const lines = built.map((b) => b.line);
      const unpriced = built.filter((b) => !(Number(b.line.list_price) > 0)).length;
      await onAddLines(lines, unpriced);
      onClose();
    } catch (_) {
      toast.error('Could not add those lines');
    } finally {
      setAdding(false);
    }
  };

  if (!isOpen) return null;

  const kindPill = (value, label) => (
    <button
      key={value}
      type="button"
      onClick={() => setKind(value)}
      className="rounded-full px-3 py-1 text-xs font-semibold border-2 transition-colors"
      style={kind === value ? { background: ORANGE, borderColor: ORANGE, color: 'white' } : { background: 'white', borderColor: '#e5e7eb', color: '#6b7280' }}
      data-testid={`ho-calc-doc-kind-${value}`}
    >
      {label}
    </button>
  );

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 p-3" style={{ zIndex: 95 }} data-testid="ho-calc-doc-overlay">
      <div className="bg-gradient-to-br from-orange-50/40 via-white to-orange-50/20 rounded-3xl shadow-2xl w-full border-2 border-orange-300 flex flex-col overflow-hidden" style={{ maxWidth: 980, maxHeight: '94vh' }}>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-orange-100 bg-white/60 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm" style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}>
              <FileText className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-extrabold text-gray-900 leading-tight">Add from Quote / Order</h2>
              <p className="text-[11px] text-gray-500">Pick a recent quote or order, tick the lines you want, add them all at once</p>
            </div>
          </div>
          <button onClick={onClose} className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-md" data-testid="ho-calc-doc-close" title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto flex-1 min-h-0">
          {/* ── Left: recent quotes / orders ── */}
          <div className={`${openDoc ? 'hidden md:block' : 'block'} rounded-xl border border-gray-200 bg-white shadow-sm p-3 min-w-0`}>
            <div className="relative">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search customer, quote or order number…"
                className="pr-9 rounded-lg border-2 border-gray-300 bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 h-10"
                data-testid="ho-calc-doc-search"
              />
              {docsLoading ? <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-3 text-gray-400" /> : <Search className="w-4 h-4 absolute right-3 top-3 text-gray-400" />}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {kindPill('all', 'All')}
              {kindPill('quote', 'Quotes')}
              {kindPill('order', 'Orders')}
              {hasCustomer && (
                <label className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scope === 'customer'}
                    onChange={(e) => setScope(e.target.checked ? 'customer' : 'all')}
                    className="w-4 h-4 accent-orange-500"
                    data-testid="ho-calc-doc-scope"
                  />
                  {customer?.name ? `${customer.name} only` : 'This customer only'}
                </label>
              )}
            </div>
            <div className="mt-2 space-y-1.5 max-h-[52vh] overflow-y-auto pr-1" data-testid="ho-calc-doc-list">
              {!docsLoading && docs.length === 0 && (
                <div className="text-center text-sm text-gray-500 py-8">
                  {scope === 'customer' && hasCustomer ? 'No quotes or orders found for this customer.' : 'No quotes or orders found.'}
                </div>
              )}
              {docs.map((d) => {
                const isChosen = openDoc && openDoc.id === d.id && openDoc.source === d.source;
                return (
                  <button
                    key={`${d.source}-${d.id}`}
                    type="button"
                    onClick={() => openDocument(d)}
                    className={`w-full text-left flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${isChosen ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:bg-orange-50 hover:border-orange-300'}`}
                    data-testid={`ho-calc-doc-${d.source}-${d.id}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                      {d.kind === 'order' ? <Package className="w-4 h-4 text-orange-600" /> : <FileText className="w-4 h-4 text-orange-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-900 truncate">{d.customer_name || 'Unnamed customer'}</div>
                      <div className="text-[11px] text-gray-500 truncate">
                        {d.kind === 'order' ? 'Order' : 'Quote'}{d.reference ? ` ${d.reference}` : ''} · {fmtDate(d.date)} · {d.line_count} line{d.line_count === 1 ? '' : 's'} · {gbp(d.total)}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Right: the lines of the chosen quote / order ── */}
          <div className={`${openDoc ? 'block' : 'hidden md:block'} rounded-xl border border-gray-200 bg-white shadow-sm p-3 min-w-0`}>
            {!openDoc ? (
              <div className="text-center text-sm text-gray-500 py-16">Choose a quote or order on the left to see its lines.</div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <button type="button" onClick={() => setOpenDoc(null)} className="md:hidden text-gray-500 hover:text-gray-800" title="Back to the list" data-testid="ho-calc-doc-back">
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-gray-900 truncate">{openDoc.customer_name || 'Unnamed customer'}</div>
                    <div className="text-[11px] text-gray-500">{openDoc.kind === 'order' ? 'Order' : 'Quote'}{openDoc.reference ? ` ${openDoc.reference}` : ''} · {fmtDate(openDoc.date)}</div>
                  </div>
                  <label className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-gray-600 cursor-pointer flex-shrink-0">
                    <input type="checkbox" checked={allChecked} onChange={toggleAll} className="w-4 h-4 accent-orange-500" data-testid="ho-calc-doc-select-all" />
                    Select all
                  </label>
                </div>
                {linesLoading ? (
                  <div className="text-sm text-gray-500 flex items-center gap-2 py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading lines…</div>
                ) : docLines.length === 0 ? (
                  <div className="text-center text-sm text-gray-500 py-10">This {openDoc.kind === 'order' ? 'order' : 'quote'} has no lines.</div>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[46vh] overflow-y-auto" data-testid="ho-calc-doc-lines">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                          <th className="text-left py-2 px-2 w-6" />
                          <th className="text-left py-2 px-2">Product</th>
                          <th className="text-right py-2 px-2">Qty</th>
                          <th className="text-right py-2 px-2">Selling / unit</th>
                          <th className="text-right py-2 px-2">Line total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {docLines.map((l, i) => (
                          <tr key={i} className="border-t border-gray-100" data-testid={`ho-calc-doc-line-${i}`}>
                            <td className="px-2 py-1.5">
                              <input
                                type="checkbox"
                                checked={!!checked[i]}
                                onChange={(e) => setChecked((prev) => ({ ...prev, [i]: e.target.checked }))}
                                className="w-4 h-4 accent-orange-500 cursor-pointer"
                                data-testid={`ho-calc-doc-line-checkbox-${i}`}
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="font-mono text-gray-800">{l.code || '—'}</div>
                              <div className="text-gray-500 truncate max-w-[200px]">{l.description}</div>
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{Number(l.quantity) % 1 === 0 ? Number(l.quantity) : Number(l.quantity).toFixed(2)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{gbp(l.selling_price)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{gbp(l.line_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="mt-2 text-[11px] text-gray-500">
                  Each line is added the same way picking a product does: list price, discount and rebate from Settings &gt; Pricing, H/O support % from the Supplier Matrix — with the selling price set to what the customer was quoted / charged.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-orange-100 bg-white/60 flex-shrink-0">
          <span className="text-xs text-gray-500">{openDoc ? `${selectedCount} of ${docLines.length} line${docLines.length === 1 ? '' : 's'} ticked` : ''}</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-xl h-10 px-4" data-testid="ho-calc-doc-cancel">Cancel</Button>
            <Button
              onClick={handleAdd}
              disabled={adding || !openDoc || selectedCount === 0}
              className="rounded-xl h-10 px-6 text-white font-semibold"
              style={{ backgroundColor: ORANGE }}
              data-testid="ho-calc-doc-add"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
              Add {selectedCount > 0 ? `${selectedCount} ` : ''}line{selectedCount === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
